import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

/**
 * Panier d'achat, persisté dans le localStorage : il survit à un rechargement
 * et à la redirection vers Stripe. On n'y stocke que le strict nécessaire ;
 * le prix et le stock sont toujours recalculés côté serveur au moment de la
 * commande (le contenu du panier ne fait pas foi pour le montant).
 */
export interface CartItem {
  productId: string;
  name: string;
  priceAmount: number; // centimes, pour l'affichage
  imageUrl?: string;
  quantity: number;
  maxQuantity: number; // stock disponible, pour borner la saisie
}

interface CartContextValue {
  items: CartItem[];
  count: number;
  totalCents: number;
  add: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  setQuantity: (productId: string, quantity: number) => void;
  remove: (productId: string) => void;
  clear: () => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

const STORAGE_KEY = 'phoelynx_cart';

const loadInitial = (): CartItem[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(loadInitial);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* localStorage indisponible : le panier reste en mémoire */
    }
  }, [items]);

  const add: CartContextValue['add'] = (item, quantity = 1) => {
    setItems((current) => {
      const existing = current.find((i) => i.productId === item.productId);
      if (existing) {
        const q = Math.min(existing.quantity + quantity, item.maxQuantity);
        return current.map((i) => (i.productId === item.productId ? { ...i, quantity: q, maxQuantity: item.maxQuantity } : i));
      }
      return [...current, { ...item, quantity: Math.min(quantity, item.maxQuantity) }];
    });
  };

  const setQuantity: CartContextValue['setQuantity'] = (productId, quantity) => {
    setItems((current) =>
      current
        .map((i) => (i.productId === productId ? { ...i, quantity: Math.max(1, Math.min(quantity, i.maxQuantity)) } : i))
        .filter((i) => i.quantity > 0)
    );
  };

  const remove: CartContextValue['remove'] = (productId) =>
    setItems((current) => current.filter((i) => i.productId !== productId));

  const clear = () => setItems([]);

  const count = items.reduce((n, i) => n + i.quantity, 0);
  const totalCents = items.reduce((sum, i) => sum + i.priceAmount * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, count, totalCents, add, setQuantity, remove, clear }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextValue => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart doit être utilisé dans un <CartProvider>');
  return ctx;
};

/** Formatage d'un montant en centimes → « 59,90 € ». */
export const formatPrice = (cents: number): string =>
  (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
